import cx from 'clsx';
import React, { FunctionComponent, useContext, useState } from 'react';
import styles from './App.module.scss';
import { ContactForm, Footer, Header } from 'components/containers';
import { CookieNotice } from 'components/presentional';
import { BrowserRouter, MemoryRouter, Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { routes } from 'routes';
import { LanguageContext } from 'context';
import { IntlProvider } from 'react-intl';
import messages from 'messages';
import { CustomArticleSlugTypes, LanguageTypes } from 'types/global';
import { WPSimplePage } from 'types/pages';
import { WPAcfGlobalPage } from 'types/acf';
import { getCachedUrl, isWebcache, useCachedPageRoutes } from 'utils/routes';
import { hasBaseline, hasLanguagePrefix, useRouteTranslations } from 'utils';
import * as idbKeyval from 'idb-keyval';
import config from 'config/config';
import { usePageClass } from 'hooks/api/usePageClass';


window.idbKeyval = idbKeyval;

interface OwnProps {}

type AppProps = JSX.IntrinsicElements['div'] & OwnProps;

const shouldRedirect = (language: LanguageTypes, location: string) => {
  if (isWebcache()) {
    return false;
  }
  if (!hasBaseline(language)) {
    if (hasLanguagePrefix(language, location)) {
      return true;
    }
    return false;
  }
  if (hasLanguagePrefix(language, location)) {
    const [first] = location.replace('/', '').split('/') as unknown as LanguageTypes[];
    return first !== language;
  }
  return true;
};

if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const App: FunctionComponent<AppProps> = props => {
  const GLOBAL_PAGE_SLUG_TYPE: CustomArticleSlugTypes = 'global_page';
  const { className, ...rest } = props;
  const { language, loading } = useContext(LanguageContext);
  const { pages, languageRoutes } = useCachedPageRoutes(language);
  const [waitForRefresh, setWaitForRefresh] = useState(false);
  const history = useHistory();
  const baseline = hasBaseline(language);
  const { translatePath } = useRouteTranslations();

  const matchBlog = useRouteMatch({
    path: '/blog',
    exact: false,
  });

  usePageClass();

  if (!languageRoutes || !languageRoutes[language] || !pages) {
    return null;
  }

  const should = shouldRedirect(language, window.location.pathname);

  if (should) {
    setWaitForRefresh(true);
    history.push(translatePath(language));
    window.location.reload();
  }

  const globalPageOptions = pages.find(page => page.slug === GLOBAL_PAGE_SLUG_TYPE) as WPSimplePage<WPAcfGlobalPage>;
  const { acf } = globalPageOptions || {};

  const appClassName = cx({
    [styles.container]: true,
    ...(className && { [className]: true }),
  });

  const appContentClassNames = cx({
    [styles.content]: true,
    [styles.loading]: loading,
  });

  if (waitForRefresh) {
    return null;
  }

  const getTranslatedPath = (path: string) => {
    const splittedPath = path.split('/');
    return splittedPath.map(path => (path.startsWith(':') || languageRoutes[language][path] === undefined ? path : languageRoutes[language][path])).join('/');
  };

  const getFullPath = (path: string) => {
    const translatedRoute = getTranslatedPath(path);
    return `${baseline ? `/${language}` : ''}/${translatedRoute}`;
  };

  const renderRoutes = () => (
      <div className={appClassName} {...rest}>
        <Header />
        <main className={appContentClassNames}>
          <Switch>
            {routes.map((route, index) => {
              // @ts-ignore
              return <Route key={`page-static-routes-${language}-${index}`} exact={true} {...route} path={getFullPath(route.path)} />;
            })}
            <Route path={`*`}>
              <Redirect to={`${baseline ? `/${language}` : ''}/${getTranslatedPath('not_found')}`} />
            </Route>
          </Switch>
          {acf?.contactForm && !matchBlog && (
              <ContactForm sticky initialyOpened={true} data-element-id="sticky-form" id="pricing" name="contact-form" data={acf.contactForm} />
          )}
          <Footer page={acf?.footer} />
          {!config.disableCookieBar && <CookieNotice />}
        </main>
      </div>
  );

  return (
      <IntlProvider defaultLocale="pl" locale={language} messages={messages[language]}>
        {isWebcache() ? <MemoryRouter initialEntries={[getCachedUrl()]}>{renderRoutes()}</MemoryRouter> : <BrowserRouter>{renderRoutes()}</BrowserRouter>}
      </IntlProvider>
  );
};

export default App;
